import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, BedDouble, UtensilsCrossed } from "lucide-react";

/**
 * CapacityMismatchModal — full-screen blocking warning.
 *
 * Shown when the user attempts to save a reservation whose room breakdown
 * (implied capacity = 1×r1 + 2×r2 + 3×r3 + 4×r4 + 5×r5) does not match the
 * total people count (adults + children + staff). Blocks the flow until the
 * user either goes back to fix or explicitly confirms they know.
 *
 * The message emphasises the business rule the user gave us verbatim:
 * "Odada kalan kişi sayısı ile yemek yiyecek kişi sayısının eşleştiğinden
 * emin olun."
 */
export default function CapacityMismatchModal({
  open, onCancel, onConfirm, roomCapacity, totalPeople,
  adults, children, staff, rooms,
}) {
  const diff = roomCapacity - totalPeople;
  const excess = diff > 0;
  const shortage = diff < 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onCancel(); }}
    >
      <DialogContent
        className="max-w-2xl w-[95vw] p-0 overflow-hidden bg-white"
        data-testid="capacity-mismatch-modal"
      >
        {/* Full-bleed hero */}
        <div className="bg-gradient-to-br from-rose-600 via-rose-500 to-orange-500 text-white p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center flex-shrink-0 shadow-md">
              <AlertTriangle size={28} className="text-white" />
            </div>
            <div className="min-w-0">
              <DialogHeader className="text-left">
                <DialogTitle className="font-serif text-2xl sm:text-3xl text-white">
                  Oda &amp; Kişi Sayısı Eşleşmiyor
                </DialogTitle>
                <DialogDescription className="text-white/95 text-sm sm:text-base mt-2 leading-relaxed">
                  <b>Odada kalan kişi sayısı ile yemek yiyecek kişi sayısının eşleştiğinden emin olun.</b>{" "}
                  Aksi hâlde yemek servisinde eksik ya da fazla porsiyon olabilir; operasyon ekibimiz bunu doğrulamak için sizinle iletişime geçecektir.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Numeric comparison panel */}
        <div className="p-6 sm:p-8 space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-2xl border-2 border-brand-navy/20 bg-canvas-cream p-5 sm:p-6 text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-navy/70 font-semibold">
                <BedDouble size={14} className="text-brand-navy" /> Odada Kalan Kişi
              </div>
              <div className="font-serif text-5xl sm:text-6xl text-brand-navy mt-2 leading-none" data-testid="capacity-modal-rooms">{roomCapacity}</div>
              <div className="text-[11px] text-slate-500 mt-2 font-mono">
                1K:{rooms?.r1 ?? 0} · 2K:{rooms?.r2 ?? 0} · 3K:{rooms?.r3 ?? 0} · 4K:{rooms?.r4 ?? 0} · 5K:{rooms?.r5 ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border-2 border-brand-orange/40 bg-brand-orange/5 p-5 sm:p-6 text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-orange-dark/80 font-semibold">
                <UtensilsCrossed size={14} className="text-brand-orange-dark" /> Yemek Yiyecek Kişi
              </div>
              <div className="font-serif text-5xl sm:text-6xl text-brand-orange-dark mt-2 leading-none" data-testid="capacity-modal-people">{totalPeople}</div>
              <div className="text-[11px] text-slate-500 mt-2">
                {adults ?? 0} yetişkin · {children ?? 0} çocuk · {staff ?? 0} görevli
              </div>
            </div>
          </div>

          <div className={`rounded-2xl px-5 py-4 text-base sm:text-lg font-semibold text-center ${
            excess ? "bg-amber-500 text-white" : "bg-rose-600 text-white"
          }`}>
            {excess && <>Fark: <b className="text-2xl mx-1">{diff}</b> kişi fazla oda kapasitesi. Odaları azaltın veya kişi sayısını artırın.</>}
            {shortage && <>Fark: <b className="text-2xl mx-1">{Math.abs(diff)}</b> kişi eksik oda kapasitesi. Ek oda ekleyin veya kişi sayısını gözden geçirin.</>}
          </div>

          <DialogFooter className="gap-2 sm:gap-3 flex-col-reverse sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="w-full sm:w-auto"
              data-testid="capacity-modal-back"
            >
              Geri Dön ve Düzelt
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              className="bg-brand-navy text-white hover:bg-brand-navy/90 w-full sm:w-auto"
              data-testid="capacity-modal-confirm"
            >
              Yine de Kaydet
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
